// view/videoControls.js — own controls for ONE <video> in the documents popup, replacing the native `controls`
// (plan bugfix_2026-10-06_permission-tagging, phase video-steuerung). Two views on the same video:
//
//   createVideoControls({ video }) → the footer bar: skip back, play/pause, skip forward (click 1 s, held faster), back
//       to the start, time "mm:ss / mm:ss" (type a time
//       + Enter jumps), sound on/off, fullscreen. Fullscreen is the video alone (like YouTube "f"); while it lasts
//       the browser's native controls are switched on — in fullscreen nobody tags, and without them there would be
//       no way to pause.
//   createVideoSeekBar({ video }) → a thin seek slider laid over the bottom edge of the video. The only overlay on the
//       video, because it belongs to it (User 2026-10-06); the stacking (above the tag layer, below an active tag
//       box) is in topo2/video-controls.css.
//
// createVideoControls also returns toggleFullscreen() — the popup binds the "F" key to it (it alone knows whether a
// tag box is being edited). Both return { element, destroy } and follow the video's own events — other code (the tag layer click, the tag list)
// may play, pause and seek the same video, and the controls stay right. Play/pause, sound and the seek slider come from
// the shared base (mediaControls.js); this file adds the video's own parts.

import { formatMmSs, parseMmSs } from '../core/timeFormat.js';
import { createMediaControlsBase, createMediaSeekBar, iconButton, setIcon, ICON_BUTTON } from './mediaControls.js';
import { attachOverflowOnMount } from './overflowBar.js';

const SIZE = 'topo2-btn--extrasmall';   // same size as the popup footer (extrasmall, User 2026-10-16)

const DEFAULT_LABELS = {   // fallbacks only — the caller passes translated labels (documents.js: VIDEO_* / SOUND_* keys)
    playPause:  'Abspielen / Pausieren',
    skipBack:    'Zurück – gedrückt halten: schneller',
    skipForward: 'Vor – gedrückt halten: schneller',
    toStart:    'Zum Anfang',
    time:       'Aktuelle Zeit – Eingabe + Enter springt zur Zeit',
    duration:   'Gesamtdauer',
    soundOff:   'Ton aus',
    soundOn:    'Ton an',
    fullscreen: 'Vollbild (Taste F)',
    seek:       'Springen',
    more:       'Weitere Bedienelemente',
};

// overflow: 'resizable' = too narrow → elements move into an extra bar behind "+" (view/overflowBar.js); 'fixed' = the
// bar stays as it is. overflowWithin: selector of the element whose width is the limit (documents.js: the footer).
export function createVideoControls({ video, labels = {}, overflow = 'resizable', overflowWithin = null } = {}) {
    const text = { ...DEFAULT_LABELS, ...labels };
    const { playBtn, skipBackBtn, skipForwardBtn, soundBtn, on, disposers } =
        createMediaControlsBase({ media: video, prefix: 'video', size: SIZE, text });

    const element = document.createElement('span');
    // Same visual block as the PDF page navigation (listNav): one connected solid frame, the time as its input group.
    element.className = 'topo2-video-controls topo2-btn-listnav topo2-listnav--solid topo2-xsmall';

    const startBtn = iconButton('topo2-video-start', text.toStart, SIZE);
    setIcon(startBtn, 'stop');

    const time = document.createElement('span');
    time.className = 'topo2-video-time topo2-listnav-inputgroup';
    const current = document.createElement('input');
    current.type = 'text';
    current.className = 'topo2-video-time-current topo2-listnav-input';
    current.title = text.time;
    current.setAttribute('aria-label', text.time);
    const duration = document.createElement('span');
    duration.className = 'topo2-video-time-duration topo2-listnav-suffix';
    duration.title = text.duration;
    time.append(current, duration);

    const fullBtn  = iconButton('topo2-video-fullscreen', text.fullscreen, SIZE);
    setIcon(fullBtn, 'maximize');

    element.append(skipBackBtn, playBtn, skipForwardBtn, startBtn, time, soundBtn, fullBtn);

    // Out first: back to the start, fullscreen, sound, the skip pair; play and the time stay (User 2026-10-14).
    if (overflow === 'resizable') {
        const folding = attachOverflowOnMount({
            bar: element,
            within: overflowWithin,
            priority: [[startBtn], [fullBtn], [soundBtn], [skipBackBtn, skipForwardBtn]],
            moreLabel: text.more,
            moreButtonClass: `${ICON_BUTTON} ${SIZE}`,
        });
        disposers.add(() => folding.destroy());
    }

    const resetTimeField = () => { current.value = formatMmSs(video.currentTime); };
    const showTime = () => { if (document.activeElement !== current) { resetTimeField(); } };
    const showDuration = () => { duration.textContent = '/' + formatMmSs(video.duration); };   // like the PDF "/3"

    on(startBtn, 'click', () => { video.pause(); video.currentTime = 0; });

    // Type a time + Enter jumps there (clamped to the video); anything that is no time puts the current time back.
    on(current, 'keydown', (ev) => {
        if (ev.key === 'Escape') { resetTimeField(); current.blur(); return; }
        if (ev.key !== 'Enter') { return; }
        ev.preventDefault();
        const seconds = parseMmSs(current.value);
        if (seconds !== null) { video.currentTime = Math.max(0, Math.min(seconds, video.duration || 0)); }
        resetTimeField();
    });
    on(current, 'blur', resetTimeField);
    // Focusing the field (click or Tab) pauses the video: the time to type should not run away. It stays paused after
    // Enter / Escape — playing again is the user's click.
    on(current, 'focus', () => video.pause());

    // Fullscreen of the video alone, with the native controls while it lasts. Only switched back off if WE switched
    // them on — a video that had controls before keeps them.
    let controlsAddedForFullscreen = false;
    const fullscreenElementNow = () => document.fullscreenElement ?? document.webkitFullscreenElement ?? null;
    const enterFullscreen = () => (video.requestFullscreen ?? video.webkitRequestFullscreen)?.call(video);
    const exitFullscreen  = () => (document.exitFullscreen ?? document.webkitExitFullscreen)?.call(document);
    const toggleFullscreen = () => { if (fullscreenElementNow() === video) { exitFullscreen(); } else { enterFullscreen(); } };
    on(fullBtn, 'click', enterFullscreen);
    const onFullscreenChange = () => {
        const fullscreenElement = fullscreenElementNow();
        if (fullscreenElement === video && !video.controls) {
            video.controls = true;
            controlsAddedForFullscreen = true;
        } else if (fullscreenElement !== video && controlsAddedForFullscreen) {
            video.controls = false;
            controlsAddedForFullscreen = false;
        }
    };
    on(document, 'fullscreenchange', onFullscreenChange);
    on(document, 'webkitfullscreenchange', onFullscreenChange);

    on(video, 'timeupdate', showTime);
    on(video, 'seeked', showTime);
    on(video, 'loadedmetadata', showDuration);
    on(video, 'durationchange', showDuration);

    showTime();
    showDuration();

    return { element, toggleFullscreen, destroy() { disposers.dispose(); } };
}

// The video popup's seek bar — a createMediaSeekBar (mediaControls.js) over the bottom edge of the video.
export function createVideoSeekBar({ video, label } = {}) { return createMediaSeekBar({ media: video, label }); }
